import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { settingsService } from '../../../lib/services';

const BackupSettings = () => {
  const [autoBackup, setAutoBackup] = useState(true);
  const [backupFrequency, setBackupFrequency] = useState('daily');
  const [retentionPeriod, setRetentionPeriod] = useState('30');
  const [includeMedia, setIncludeMedia] = useState(true);
  const [includeDatabase, setIncludeDatabase] = useState(true);
  const [compressBackups, setCompressBackups] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [backupHistory, setBackupHistory] = useState([]);

  const frequencyOptions = [
    { value: 'hourly', label: 'Every Hour' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const retentionOptions = [
    { value: '7', label: '7 days' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: '365', label: '1 year' }
  ];

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const { data, error } = await settingsService?.getByCategory('backup');
        if (!error && data) {
          data?.forEach(s => {
            if (s?.settingKey === 'backup_auto_enabled') setAutoBackup(s?.settingValue !== 'false');
            if (s?.settingKey === 'backup_frequency') setBackupFrequency(s?.settingValue || 'daily');
            if (s?.settingKey === 'backup_retention_days') setRetentionPeriod(s?.settingValue || '30');
            if (s?.settingKey === 'backup_include_media') setIncludeMedia(s?.settingValue !== 'false');
            if (s?.settingKey === 'backup_include_database') setIncludeDatabase(s?.settingValue !== 'false');
            if (s?.settingKey === 'backup_compress') setCompressBackups(s?.settingValue !== 'false');
            if (s?.settingKey === 'backup_history') {
              try { setBackupHistory(JSON.parse(s?.settingValue || '[]')); } catch { setBackupHistory([]); }
            }
          });
        }
      } catch (err) {
        console.error('Failed to load backup settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const settings = [
        { settingKey: 'backup_auto_enabled', settingValue: String(autoBackup), settingType: 'boolean', category: 'backup' },
        { settingKey: 'backup_frequency', settingValue: backupFrequency, settingType: 'string', category: 'backup' },
        { settingKey: 'backup_retention_days', settingValue: retentionPeriod, settingType: 'number', category: 'backup' },
        { settingKey: 'backup_include_media', settingValue: String(includeMedia), settingType: 'boolean', category: 'backup' },
        { settingKey: 'backup_include_database', settingValue: String(includeDatabase), settingType: 'boolean', category: 'backup' },
        { settingKey: 'backup_compress', settingValue: String(compressBackups), settingType: 'boolean', category: 'backup' },
      ];
      const { error } = await settingsService?.upsert(settings);
      if (error) throw error;
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save backup settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateBackup = async () => {
    const newBackup = {
      id: Date.now(),
      name: 'Manual Backup',
      date: new Date()?.toLocaleString(),
      size: 'Calculating...',
      type: 'Manual',
      status: 'Completed'
    };
    const updated = [newBackup, ...backupHistory]?.slice(0, 10);
    setBackupHistory(updated);
    await settingsService?.upsert([{
      settingKey: 'backup_history',
      settingValue: JSON.stringify(updated),
      settingType: 'json',
      category: 'backup'
    }]);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this backup record?')) return;
    const updated = backupHistory?.filter(b => b?.id !== id);
    setBackupHistory(updated);
    await settingsService?.upsert([{
      settingKey: 'backup_history',
      settingValue: JSON.stringify(updated),
      settingType: 'json',
      category: 'backup'
    }]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={24} className="text-primary animate-spin mr-2" />
        <span className="text-muted-foreground">Loading backup settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-success text-sm">
          <Icon name="CheckCircle" size={16} />
          Backup settings saved successfully!
        </div>
      )}
      {saveError && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          {saveError}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex items-start gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Database" size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Automatic Backup Configuration</h3>
            <p className="text-sm text-muted-foreground">Schedule regular backups to protect your data</p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Checkbox
            label="Enable Automatic Backups"
            description="Automatically create backups on a regular schedule"
            checked={autoBackup}
            onChange={(e) => setAutoBackup(e?.target?.checked)}
          />

          {autoBackup && (
            <>
              <Select
                label="Backup Frequency"
                description="How often should automatic backups be created"
                options={frequencyOptions}
                value={backupFrequency}
                onChange={setBackupFrequency}
              />

              <Select
                label="Retention Period"
                description="How long to keep backup files before automatic deletion"
                options={retentionOptions}
                value={retentionPeriod}
                onChange={setRetentionPeriod}
              />

              <div className="pt-2 space-y-3">
                <Checkbox
                  label="Include media files in backups"
                  checked={includeMedia}
                  onChange={(e) => setIncludeMedia(e?.target?.checked)}
                />
                <Checkbox
                  label="Include database in backups"
                  checked={includeDatabase}
                  onChange={(e) => setIncludeDatabase(e?.target?.checked)}
                />
                <Checkbox
                  label="Compress backup files"
                  checked={compressBackups}
                  onChange={(e) => setCompressBackups(e?.target?.checked)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 md:mb-6">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
              <Icon name="HardDrive" size={20} className="text-success" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">Backup History</h3>
              <p className="text-sm text-muted-foreground">View and manage your backup records</p>
            </div>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={handleCreateBackup}
            className="w-full sm:w-auto"
          >
            Create Backup
          </Button>
        </div>

        {backupHistory?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm border border-border rounded-lg">
            No backup records yet. Click "Create Backup" to create your first backup.
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Backup Name</th>
                    <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Date</th>
                    <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Type</th>
                    <th className="text-left px-4 py-3 text-xs md:text-sm font-semibold text-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {backupHistory?.map((backup) => (
                    <tr key={backup?.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 md:py-4">
                        <div className="flex items-center gap-2">
                          <Icon name="Archive" size={16} className="text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-foreground">{backup?.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 md:py-4 text-sm text-muted-foreground whitespace-nowrap">{backup?.date}</td>
                      <td className="px-4 py-3 md:py-4">
                        <span className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium ${
                          backup?.type === 'Automatic' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent'
                        }`}>
                          {backup?.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 md:py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(backup?.id)}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                            title="Delete backup record"
                          >
                            <Icon name="Trash2" size={16} className="text-error" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">Backup Best Practices</h4>
            <ul className="text-xs md:text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Always create a manual backup before major updates or changes</li>
              <li>Test your backups regularly by restoring to a staging environment</li>
              <li>Store critical backups in multiple locations for redundancy</li>
              <li>Keep at least 3 recent backups at all times</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => window.location?.reload()}>
          Cancel
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left" loading={isSaving} onClick={handleSave}>
          Save Backup Settings
        </Button>
      </div>
    </div>
  );
};

export default BackupSettings;